import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../../components/layout/Navbar";
import Footer from "../../components/layout/Footer";
import Select from "react-select";
import { decryptData } from "../../utils/encrypt";
import {
  SALES_ORDER_CUSTOMER_LIST,
  MATERIAL_LIST_ENDPOINT,
  GET_LOADING_POINTS_ENDPOINT,
  PACKING_TYPE_LIST_ENDPOINT,
  LOGISTIC_PLANNING_FILTERS_API,
} from "../../config/api";
import LogisticPlanningTable from "./LogisticPlanningTable";

function LogisticPlanning() {
  // --- helpers: date format + default window
  const toYMD = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const computeDefaultDates = () => {
    const now = new Date();
    const from = new Date(now);
    from.setDate(from.getDate() - 7); // last 7 days
    const to = new Date(now);
    to.setDate(to.getDate() + 21); // next 3 weeks
    return { from: toYMD(from), to: toYMD(to) };
  };

  // --- filters with defaults (focused for logistics)
  const [filters, setFilters] = useState(() => {
    const { from, to } = computeDefaultDates();
    return {
      customer_id: "",
      material_id: "",
      packing_type: "",
      loading_point_id: "",
      delivery_from: from,
      delivery_to: to,
      // explicitly show only Pending by default for logistics queue
      logistic_status: "Pending", // "", "Pending", "Done"
    };
  });

  // --- lookups
  const [lookups, setLookups] = useState({
    customers: [],
    materials: [],
    packingTypes: ["25 Kgs Bags", "50 Kgs Bag", "Loose Bulker"],
    loadingPoints: [],
  });
  const [packingOptions, setPackingOptions] = useState([]);

  // --- data & user/org
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState(null);
  const [orgId, setOrgId] = useState(null);

  // packing types (dynamic)
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(PACKING_TYPE_LIST_ENDPOINT);
        const data = await res.json(); // expect [{id, name}] or [{id, packing_type}]
        setPackingOptions(Array.isArray(data) ? data : []);
      } catch {
        setPackingOptions([]);
      }
    })();
  }, []);

  // load user/org
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem("suyog_user");
      if (storedUser) {
        const u = decryptData(storedUser);
        setUserId(u?.user_id || null);
      }
    } catch (e) {
      console.error("Token decrypt failed:", e);
    }
    const storedOrg = localStorage.getItem("suyog_selected_org");
    if (storedOrg) {
      try {
        const parsedOrg = JSON.parse(storedOrg);
        setOrgId(parsedOrg?.value || null);
      } catch (e) {
        console.error("suyog_selected_org parse error:", e);
      }
    }
  }, []);

  // load dropdowns + first fetch
  useEffect(() => {
    if (!userId || !orgId) return;
    const loadDropdowns = async () => {
      try {
        const cRes = await fetch(SALES_ORDER_CUSTOMER_LIST, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: userId }),
        });
        const cJson = await cRes.json();
        const customers = Array.isArray(cJson) ? cJson : cJson?.data || [];
        setLookups((prev) => ({ ...prev, customers }));

        const mRes = await fetch(MATERIAL_LIST_ENDPOINT);
        const mJson = await mRes.json();
        const materials = Array.isArray(mJson) ? mJson : mJson?.data || [];
        setLookups((prev) => ({ ...prev, materials }));

        const lpRes = await fetch(GET_LOADING_POINTS_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ org_id: orgId }),
        });
        const lpJson = await lpRes.json();
        const loadingPoints =
          lpJson?.success && Array.isArray(lpJson.records) ? lpJson.records : [];
        setLookups((prev) => ({ ...prev, loadingPoints }));
      } catch (e) {
        console.error("Dropdowns load error:", e);
      }
    };
    loadDropdowns();
    fetchReport(true); // first load with defaults
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, orgId]);

  // fetch list
  const fetchReport = async (withFilters = true) => {
    if (!userId || !orgId) return;
    setLoading(true);
    try {
      const res = await fetch(LOGISTIC_PLANNING_FILTERS_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          org_id: orgId,
          include_vehicle_details: true,
          ...(withFilters ? filters : {}),
        }),
      });
      const data = await res.json();
      if (data?.success) setRows(data.rows || []);
      else setRows([]);
    } catch (err) {
      console.error("Logistics fetch error:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((f) => ({ ...f, [name]: value }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchReport(true);
  };

  const handleReset = () => {
    const { from, to } = computeDefaultDates();
    setFilters((f) => ({
      ...f,
      customer_id: "",
      material_id: "",
      packing_type: "",
      loading_point_id: "",
      delivery_from: from,
      delivery_to: to,
      logistic_status: "Pending",
    }));
    fetchReport(true);
  };

  // react-select options
  const customerOptions = useMemo(
    () =>
      (lookups.customers || []).map((c) => ({
        value: String(c.id),
        label: c.account_name,
      })),
    [lookups.customers]
  );

  const selectedCustomerOption = useMemo(
    () =>
      customerOptions.find(
        (o) => String(o.value) === String(filters.customer_id || "")
      ) || null,
    [customerOptions, filters.customer_id]
  );

  return (
    <>
      <Navbar />
      <div className="container mt-4">
        <h4>🚚 Logistic Planning</h4>

        {/* Filters */}
        <form className="row g-3 mt-2" onSubmit={handleSearch}>
          <div className="col-md-3">
            <label className="form-label">Customer</label>
            <Select
              options={customerOptions}
              value={selectedCustomerOption}
              onChange={(opt) =>
                setFilters((f) => ({
                  ...f,
                  customer_id: opt ? String(opt.value) : "",
                }))
              }
              isClearable
              isSearchable
              placeholder="-- All Customers --"
              classNamePrefix="react-select"
            />
          </div>

          <div className="col-md-3">
            <label className="form-label">Material</label>
            <select
              className="form-select"
              name="material_id"
              value={filters.material_id}
              onChange={handleChange}
            >
              <option value="">-- All Materials --</option>
              {lookups.materials.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.material_name}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label">Packing</label>
            <select
              className="form-select"
              name="packing_type"
              value={filters.packing_type}
              onChange={handleChange}
            >
              <option value="" disabled={!packingOptions.length}>
                {packingOptions.length ? "-- All Packing --" : "Loading..."}
              </option>

              {packingOptions.map((p) => {
                const label = p.name ?? p.packing_type;
                return (
                  <option key={p.id ?? label} value={label}>
                    {label}
                  </option>
                );
              })}

              {packingOptions.length === 0 &&
                lookups.packingTypes.map((txt) => (
                  <option key={txt} value={txt}>
                    {txt}
                  </option>
                ))}
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label">Loading Point</label>
            <select
              className="form-select"
              name="loading_point_id"
              value={filters.loading_point_id}
              onChange={handleChange}
            >
              <option value="">-- All Loading Points --</option>
              {lookups.loadingPoints.map((lp) => (
                <option key={lp.id} value={lp.id}>
                  {lp.name}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label">Delivery From</label>
            <input
              type="date"
              className="form-control"
              name="delivery_from"
              value={filters.delivery_from}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-3">
            <label className="form-label">Delivery To</label>
            <input
              type="date"
              className="form-control"
              name="delivery_to"
              value={filters.delivery_to}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-2">
            <label className="form-label">Logistic Status</label>
            <select
              className="form-select"
              name="logistic_status"
              value={filters.logistic_status}
              onChange={handleChange}
            >
              <option value="">All</option>
              <option>Pending</option>
              <option>Done</option>
            </select>
          </div>

          <div className="col-12 d-flex gap-2">
            <button className="btn btn-primary" type="submit" disabled={loading}>
              {loading ? "Loading..." : "Search"}
            </button>
            <button className="btn btn-secondary" type="button" onClick={handleReset}>
              Reset
            </button>
          </div>
        </form>

        {/* Table */}
        <LogisticPlanningTable rows={rows} />
      </div>
      <Footer />
    </>
  );
}

export default LogisticPlanning;
